import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import {
    FAQ,
    FAQFilters
} from '@/types/faq'
import { faqsApi } from '@/services/api/faqs'

interface FAQState {
    // List view state
    faqs: FAQ[]
    isLoading: boolean
    error: string | null
    pagination: {
        currentPage: number
        totalPages: number
        totalItems: number
        itemsPerPage: number
    }

    // Filter state
    activeFilters: FAQFilters
    searchQuery: string
    activeCategory: string // Category ID or 'all'

    // Engagement
    helpfulFAQs: string[]
}

const initialState: FAQState = {
    faqs: [],
    isLoading: false,
    error: null,
    pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
    },
    activeFilters: { page: 1, limit: 10 },
    searchQuery: '',
    activeCategory: 'all',
    helpfulFAQs: [],
}

// ===== ASYNC THUNKS =====

export const fetchFAQs = createAsyncThunk(
    'faq/fetchFAQs',
    async (filters: FAQFilters = {}, { rejectWithValue }) => {
        try {
            const response = await faqsApi.getFAQs(filters)
            const data = response.data
            return {
                faqs: data.faqs || [],
                pagination: data.pagination || {
                    currentPage: 1,
                    totalPages: 1,
                    totalItems: 0,
                    itemsPerPage: 10,
                }
            }
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to fetch FAQs'
            return rejectWithValue(message)
        }
    }
)

export const searchFAQs = createAsyncThunk(
    'faq/searchFAQs',
    async (params: { query: string; filters?: FAQFilters }, { rejectWithValue }) => {
        try {
            const response = await faqsApi.searchFAQs(params.query, params.filters)
            const data = response.data
            return {
                faqs: data.faqs || [],
                pagination: data.pagination || {
                    currentPage: 1,
                    totalPages: 1,
                    totalItems: 0,
                    itemsPerPage: 10,
                }
            }
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to search FAQs'
            return rejectWithValue(message)
        }
    }
)

export const fetchFAQsByCategory = createAsyncThunk(
    'faq/fetchFAQsByCategory',
    async (params: { category: string; filters?: FAQFilters }, { rejectWithValue }) => {
        try {
            const response = await faqsApi.getFAQsByCategory(params.category, params.filters)
            const data = response.data
            return {
                faqs: data.faqs || [],
                pagination: data.pagination || {
                    currentPage: 1,
                    totalPages: 1,
                    totalItems: 0,
                    itemsPerPage: 10,
                }
            }
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to fetch FAQs by category'
            return rejectWithValue(message)
        }
    }
)

export const trackFAQView = createAsyncThunk(
    'faq/trackFAQView',
    async (faqId: string, { rejectWithValue }) => {
        try {
            const response = await faqsApi.trackFAQView(faqId)
            return { faqId, faq: response.data.faq }
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to track FAQ view'
            return rejectWithValue(message)
        }
    }
)

export const markFAQHelpful = createAsyncThunk(
    'faq/markFAQHelpful',
    async (faqId: string, { rejectWithValue }) => {
        try {
            await faqsApi.markFAQHelpful(faqId)
            return faqId
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to mark FAQ as helpful'
            return rejectWithValue(message)
        }
    }
)

// ===== SLICE =====

const faqSlice = createSlice({
    name: 'faq',
    initialState,
    reducers: {
        setActiveCategory: (state, action: PayloadAction<string>) => {
            state.activeCategory = action.payload
        },
        setSearchQuery: (state, action: PayloadAction<string>) => {
            state.searchQuery = action.payload
        },
        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.pagination.currentPage = action.payload
        },
        setActiveFilters: (state, action: PayloadAction<FAQFilters>) => {
            state.activeFilters = action.payload
        },
        clearSearch: (state) => {
            state.searchQuery = ''
        },
        resetFAQs: (state) => {
            state.faqs = []
            state.error = null
            state.pagination = initialState.pagination
        },
    },
    extraReducers: (builder) => {
        // Fetch FAQs
        builder
            .addCase(fetchFAQs.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchFAQs.fulfilled, (state, action) => {
                state.isLoading = false
                state.faqs = action.payload.faqs
                state.pagination = action.payload.pagination
            })
            .addCase(fetchFAQs.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Search FAQs
        builder
            .addCase(searchFAQs.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(searchFAQs.fulfilled, (state, action) => {
                state.isLoading = false
                state.faqs = action.payload.faqs
                state.pagination = action.payload.pagination
            })
            .addCase(searchFAQs.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Fetch FAQs by Category
        builder
            .addCase(fetchFAQsByCategory.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchFAQsByCategory.fulfilled, (state, action) => {
                state.isLoading = false
                state.faqs = action.payload.faqs
                state.pagination = action.payload.pagination
            })
            .addCase(fetchFAQsByCategory.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Track FAQ View
        builder
            .addCase(trackFAQView.fulfilled, (state, action) => {
                // Update the FAQ in the list with the new view count
                const faqIndex = state.faqs.findIndex(faq => faq._id === action.payload.faqId)
                if (faqIndex !== -1) {
                    state.faqs[faqIndex] = { ...state.faqs[faqIndex], views: action.payload.faq.views }
                }
            })

        // Mark FAQ as Helpful
        builder
            .addCase(markFAQHelpful.fulfilled, (state, action) => {
                if (!state.helpfulFAQs.includes(action.payload)) {
                    state.helpfulFAQs.push(action.payload)
                    // Update the FAQ in the list with incremented helpful count
                    const faqIndex = state.faqs.findIndex(faq => faq._id === action.payload)
                    if (faqIndex !== -1) {
                        state.faqs[faqIndex] = { ...state.faqs[faqIndex], helpful: state.faqs[faqIndex].helpful + 1 }
                    }
                }
            })
    },
})

export const {
    setActiveCategory,
    setSearchQuery,
    setCurrentPage,
    setActiveFilters,
    clearSearch,
    resetFAQs,
} = faqSlice.actions

export default faqSlice.reducer